/**
 * « Mes abonnements » — profil du client (14/10/2026).
 *
 * Philippe, sur son profil : « je m'interroge si je suis abonné e-mail et notif,
 * j'ai du mal à obtenir l'information ». La page ne montrait que l'interrupteur
 * push de l'appareil ; l'abonnement aux actualités par email n'apparaissait
 * nulle part. Ici : actualités par email (abonné / en attente / non, avec un
 * interrupteur) et emails de commande (toujours envoyés — on le dit).
 */
import { useEffect, useState } from 'react';
import api from '../../services/api';

type Statut = 'CONFIRMED' | 'PENDING' | 'UNSUBSCRIBED' | null;

export default function MesAbonnements() {
  const [chargement, setChargement] = useState(true);
  const [email, setEmail] = useState<string | null>(null);
  const [statut, setStatut] = useState<Statut>(null);
  const [envoi, setEnvoi] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const lire = async () => {
    try {
      const r: any = await api.get('/newsletter/me');
      const d = r?.data ?? r;
      setEmail(d?.email ?? null);
      setStatut(d?.statut ?? null);
    } catch {
      setMessage('État de l’abonnement illisible pour le moment.');
    } finally {
      setChargement(false);
    }
  };
  useEffect(() => { lire(); }, []);

  const abonne = statut === 'CONFIRMED';
  const basculer = async () => {
    setEnvoi(true);
    setMessage(null);
    try {
      if (abonne) {
        if (!confirm('Ne plus recevoir les actualités du magasin par email ?')) { setEnvoi(false); return; }
        await api.post('/newsletter/me/unsubscribe');
        setStatut('UNSUBSCRIBED');
        setMessage('C’est fait : vous ne recevrez plus les actualités par email.');
      } else {
        const r: any = await api.post('/newsletter/me/subscribe');
        const d = r?.data ?? r;
        if (d?.status === 'already_confirmed') {
          setStatut('CONFIRMED');
        } else if (d?.status === 'sans_email') {
          setMessage('Votre compte n’a pas d’adresse email : ajoutez-en une à votre profil.');
        } else {
          setStatut('PENDING');
          setMessage(`Un email de confirmation vient de partir à ${email} : cliquez sur le lien qu’il contient.`);
        }
      }
    } catch (e: any) {
      setMessage(e?.response?.data?.message || 'Action impossible pour le moment, réessayez.');
    } finally {
      setEnvoi(false);
    }
  };

  const etiquette = abonne
    ? 'Abonné — vous recevez le résumé hebdomadaire et les actualités'
    : statut === 'PENDING'
      ? 'En attente de confirmation — cliquez sur le lien reçu par email'
      : 'Pas abonné';

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Mes abonnements</h2>
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-4 p-4 bg-gray-50 rounded-xl">
          <div className="min-w-0">
            <div className="font-medium text-gray-900">Actualités du magasin par email</div>
            <div className={`text-sm ${abonne ? 'text-green-700' : statut === 'PENDING' ? 'text-amber-700' : 'text-gray-600'}`}>
              {chargement ? 'Lecture…' : email ? etiquette : 'Pas d’adresse email sur votre compte'}
            </div>
            {email && <div className="text-xs text-gray-500 mt-0.5 truncate">{email}</div>}
          </div>
          {email && (
            <button
              onClick={basculer}
              disabled={chargement || envoi}
              role="switch"
              aria-checked={abonne}
              aria-label="Actualités du magasin par email"
              className={`relative w-12 h-7 rounded-full transition-colors shrink-0 ${abonne ? 'bg-green-500' : 'bg-gray-300'} ${envoi ? 'opacity-50' : ''}`}
            >
              <span
                className="absolute top-1 left-1 w-5 h-5 bg-white rounded-full shadow transition-transform"
                style={{ transform: abonne ? 'translateX(20px)' : 'translateX(0)' }}
              />
            </button>
          )}
        </div>
        {statut === 'PENDING' && email && (
          <button onClick={basculer} disabled={envoi} className="text-sm text-green-700 font-semibold underline">
            Renvoyer l’email de confirmation
          </button>
        )}
        <div className="p-4 bg-gray-50 rounded-xl">
          <div className="font-medium text-gray-900">Emails de commande</div>
          <div className="text-sm text-gray-600">
            Toujours envoyés (confirmation de paiement, commande prête) : ils font partie de votre commande.
          </div>
        </div>
        {message && <p className="text-sm text-gray-700">{message}</p>}
      </div>
    </div>
  );
}
